import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { toastMessage } from '../../i18n/toastMessages';
import {
  BILLING_ERROR_CODES,
  BillingError,
  BillingPrices,
  BillingSubscription,
  cancelBannerDate,
  createCheckoutSession,
  createPortalSession,
  getPrices,
  getSubscription,
} from '../../services/billingService';
import { getPixelsSummary } from '../../services/pixelsService';
import CancelSubscriptionButton from './CancelSubscriptionButton';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import PixleTransparentSrc from '../../assets/icons/logo/pixle-transparent.png';
import './billing.css';

const MIN_PACK_QUANTITY = 1;
const MAX_PACK_QUANTITY = 25;

const clampQuantity = (value: number): number => {
  if (!Number.isFinite(value)) return MIN_PACK_QUANTITY;
  return Math.min(
    MAX_PACK_QUANTITY,
    Math.max(MIN_PACK_QUANTITY, Math.floor(value))
  );
};

/** Format a price with its currency; nulls render as an em dash. */
const formatMoney = (price: number | null, currency: string | null): string => {
  if (price === null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency ?? 'USD').toUpperCase(),
      maximumFractionDigits: price % 1 === 0 ? 0 : 2,
    }).format(price);
  } catch {
    return `${price}${currency ? ` ${currency.toUpperCase()}` : ''}`;
  }
};

const formatRenewalDate = (iso: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const capitalize = (value: string | null): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '—';

const PLAN_FEATURES = [
  'Create animation with leading AI video models',
  'Build longer, consistent animated stories',
  'Commercial use',
  'Unused Pixles roll over for one month',
  'Cancel anytime',
];

const PLAN_TAGLINES: Record<string, string> = {
  pro: 'For creators producing regularly',
  petite:
    'For short projects or trying Pixle on a real production use case',
};

const PLAN_RUNTIME: Record<string, string> = {
  pro: '≈ 4–5 min of video generation*',
  petite: '≈ 45 sec – 1 min of video generation*',
};

const isSubscriptionActive = (status: string | null): boolean => {
  const normalized = status?.toLowerCase();
  return normalized === 'active' || normalized === 'trialing';
};

const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const [prices, setPrices] = useState<BillingPrices | null>(null);
  const [subscription, setSubscription] = useState<BillingSubscription | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Set when the backend blocks top-ups with 403 TRIAL_ACTIVE: the Buy
  // button is replaced with a "Subscribe to Pro" CTA.
  const [trialBlocked, setTrialBlocked] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const refreshSubscription = async (token: string): Promise<void> => {
    // Do not cache across sessions — always refetch the source of truth.
    const loaded = await getSubscription(token).catch(() => null);
    setSubscription(loaded);
    // Keep the access badge consistent; access stays allowed until period end.
    await getPixelsSummary(token).catch(() => null);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      setError('Please log in to manage billing.');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [loadedPrices, loadedSubscription] = await Promise.all([
          getPrices(token),
          getSubscription(token).catch(() => null),
        ]);
        if (cancelled) return;
        setPrices(loadedPrices);
        setSubscription(loadedSubscription);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load billing.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getTokenOrRedirect = (): string | null => {
    const token = localStorage.getItem('authToken');
    if (!token) navigate('/login');
    return token;
  };

  const handleBuyPacks = async () => {
    const token = getTokenOrRedirect();
    if (!token) return;
    setBusyAction('buy');
    setActionError(null);
    try {
      const session = await createCheckoutSession(token, {
        mode: 'pixel_purchase',
        quantity: clampQuantity(quantity),
      });
      window.location.href = session.url;
    } catch (err) {
      if (
        err instanceof BillingError &&
        (err.code === BILLING_ERROR_CODES.TRIAL_ACTIVE || err.status === 403)
      ) {
        setTrialBlocked(true);
      } else {
        setActionError(
          err instanceof Error ? err.message : 'Failed to start checkout.'
        );
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleSubscribe = async (planId: string) => {
    const token = getTokenOrRedirect();
    if (!token) return;
    setBusyAction(`subscribe:${planId}`);
    setActionError(null);
    try {
      const session = await createCheckoutSession(token, {
        mode: 'subscription',
        planId,
      });
      window.location.href = session.url;
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to start checkout.'
      );
    } finally {
      setBusyAction(null);
    }
  };

  const handleManage = async () => {
    const token = getTokenOrRedirect();
    if (!token) return;
    setBusyAction('portal');
    setActionError(null);
    try {
      const session = await createPortalSession(token);
      window.location.href = session.url;
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to open the portal.'
      );
    } finally {
      setBusyAction(null);
    }
  };

  const handleCancelled = (view: BillingSubscription) => {
    setCancelOpen(false);
    setSubscription(view);
    toast.success(
      toastMessage('billing.cancellationScheduled', {
        date: cancelBannerDate(view.currentPeriodEnd),
      })
    );
    const token = localStorage.getItem('authToken');
    if (token) void refreshSubscription(token);
  };

  const handleAlreadyCancelled = () => {
    setCancelOpen(false);
    const token = localStorage.getItem('authToken');
    if (token) void refreshSubscription(token);
  };

  const subscriptionActive = isSubscriptionActive(subscription?.status ?? null);
  // Fallback plan id comes from the API-loaded plans; "pro" is the backend
  // contract default when no plan has loaded yet.
  const defaultPlanId = prices?.subscriptions[0]?.id ?? 'pro';
  const pack = prices?.pixelPurchase;
  const packTotal =
    pack?.pricePerPack !== null &&
    pack?.pricePerPack !== undefined &&
    Number.isFinite(pack.pricePerPack)
      ? pack.pricePerPack * clampQuantity(quantity)
      : null;

  return (
    <div className="billing-page">
      <header className="billing-header">
        <button
          type="button"
          className="billing-secondary"
          onClick={() => navigate('/')}
          aria-label="Back to home"
        >
          ← Back
        </button>
        <div>
          <img src={PixleTransparentSrc} className="billing-logo" alt="Pixle" />
          <h1>Billing</h1>
          <p className="billing-subtitle">
            Subscribe for monthly pixles or top up with pixle packs.
          </p>
        </div>
      </header>

      {loading && (
        <div aria-busy="true" aria-label="Loading billing">
          <section className="billing-card">
            <div className="billing-skeleton-line billing-skeleton-line-title" />
            <div className="billing-skeleton-line" />
            <div className="billing-skeleton-line" />
            <div className="billing-skeleton-line billing-skeleton-line-short" />
          </section>
          <section className="billing-card">
            <div className="billing-skeleton-line billing-skeleton-line-title" />
            <div className="billing-skeleton-line" />
            <div className="billing-skeleton-line billing-skeleton-line-short" />
          </section>
        </div>
      )}
      {!loading && error && (
        <p className="billing-error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && (
        <>
          {actionError && (
            <p className="billing-error" role="alert">
              {actionError}
            </p>
          )}

          {subscriptionActive && subscription && (
            <section className="billing-card" aria-label="Current subscription">
              <h2>Current subscription</h2>
              {subscription.cancelAtPeriodEnd ? (
                <div
                  className="billing-cancel-banner"
                  role="status"
                  aria-live="polite"
                >
                  <p>
                    Active · cancels{' '}
                    {cancelBannerDate(subscription.currentPeriodEnd)}
                  </p>
                  <p>
                    You keep access and your banked pixles until then. To
                    restart after{' '}
                    {cancelBannerDate(subscription.currentPeriodEnd)}, choose a
                    plan below.
                  </p>
                </div>
              ) : null}
              <dl className="billing-facts">
                <div>
                  <dt>Plan</dt>
                  <dd>{capitalize(subscription.plan)}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{capitalize(subscription.status)}</dd>
                </div>
                <div>
                  <dt>Renews</dt>
                  <dd>{formatRenewalDate(subscription.currentPeriodEnd)}</dd>
                </div>
                {subscription.cancelAtPeriodEnd && (
                  <div>
                    <dt>Cancellation</dt>
                    <dd>Cancels at the end of the period</dd>
                  </div>
                )}
              </dl>
              <div className="billing-actions">
                <button
                  type="button"
                  className="billing-primary"
                  onClick={handleManage}
                  disabled={busyAction === 'portal'}
                >
                  {busyAction === 'portal' ? 'Opening…' : 'Manage subscription'}
                </button>
                {!subscription.cancelAtPeriodEnd && (
                  <CancelSubscriptionButton
                    onClick={() => setCancelOpen(true)}
                  />
                )}
              </div>
              <CancelSubscriptionModal
                open={cancelOpen}
                periodEnd={subscription.currentPeriodEnd}
                onClose={() => setCancelOpen(false)}
                onCancelled={handleCancelled}
                onAlreadyCancelled={handleAlreadyCancelled}
              />
            </section>
          )}

          {(!subscriptionActive || subscription?.cancelAtPeriodEnd) &&
            prices &&
            prices.subscriptions.length > 0 && (
            <section className="billing-card" aria-label="Subscription plans">
              <h2>Subscribe</h2>
              {(() => {
                const proPlan =
                  prices.subscriptions.find((p) => p.id === 'pro') ??
                  prices.subscriptions[0];
                const otherPlans = prices.subscriptions.filter(
                  (p) => p.id !== proPlan.id
                );
                const proBusy = busyAction === `subscribe:${proPlan.id}`;
                const proPrice = formatMoney(proPlan.price, proPlan.currency);
                return (
                  <>
                    <article
                      key={proPlan.id}
                      className="billing-plan billing-plan-featured"
                    >
                      <div>
                        <strong className="billing-plan-name">Pixle Pro</strong>
                        <span className="billing-plan-price">
                          {proPrice}
                          {proPlan.interval ? ` / ${proPlan.interval}` : ''}
                        </span>
                        <span className="billing-plan-allowance">
                          {proPlan.pixelsPerPeriod !== null
                            ? `${proPlan.pixelsPerPeriod.toLocaleString()} Pixles every month`
                            : 'Pixles every month'}
                          {(PLAN_RUNTIME[proPlan.id] ?? PLAN_RUNTIME.pro) &&
                            ` · ${PLAN_RUNTIME[proPlan.id] ?? PLAN_RUNTIME.pro}`}
                        </span>
                        <span className="billing-plan-tagline">
                          {PLAN_TAGLINES[proPlan.id] ?? PLAN_TAGLINES.pro}
                        </span>
                        <ul className="billing-plan-features">
                          {PLAN_FEATURES.map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                      <button
                        type="button"
                        className="billing-primary billing-plan-cta"
                        onClick={() => handleSubscribe(proPlan.id)}
                        disabled={proBusy}
                      >
                        {proBusy
                          ? 'Redirecting…'
                          : `Upgrade to Pixle Pro — ${proPrice}/month`}
                      </button>
                    </article>
                    {otherPlans.map((plan) => (
                      <article
                        key={plan.id}
                        className="billing-plan billing-plan-featured"
                      >
                        <div>
                          <strong className="billing-plan-name">
                            Pixle{' '}
                            {plan.id.charAt(0).toUpperCase() + plan.id.slice(1)}
                          </strong>
                          <span className="billing-plan-price">
                            {formatMoney(plan.price, plan.currency)}
                            {plan.interval ? ` / ${plan.interval}` : ''}
                          </span>
                          <span className="billing-plan-allowance">
                            {plan.pixelsPerPeriod !== null
                              ? `${plan.pixelsPerPeriod.toLocaleString()} Pixles every month`
                              : 'Pixles included'}
                            {PLAN_RUNTIME[plan.id] &&
                              ` · ${PLAN_RUNTIME[plan.id]}`}
                          </span>
                          {PLAN_TAGLINES[plan.id] && (
                            <span className="billing-plan-tagline">
                              {PLAN_TAGLINES[plan.id]}
                            </span>
                          )}
                          <ul className="billing-plan-features">
                            {PLAN_FEATURES.map((feature) => (
                              <li key={feature}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                        <button
                          type="button"
                          className="billing-primary billing-plan-cta"
                          onClick={() => handleSubscribe(plan.id)}
                          disabled={busyAction === `subscribe:${plan.id}`}
                        >
                          {busyAction === `subscribe:${plan.id}`
                            ? 'Redirecting…'
                            : `Upgrade to Pixle ${plan.id.charAt(0).toUpperCase() + plan.id.slice(1)} — ${formatMoney(plan.price, plan.currency)}/month`}
                        </button>
                      </article>
                    ))}
                  </>
                );
              })()}
              <p className="billing-reassurance">
                Secure checkout powered by Stripe. We never store your card
                details.
              </p>
              <p className="billing-reassurance">
                *Video length is approximate — model and resolution dependent.
              </p>
              <p className="billing-support">
                Questions before subscribing?{' '}
                <a href="mailto:info@pixle.pro">info@pixle.pro</a>
              </p>
            </section>
          )}

          <section className="billing-card" aria-label="Pixel packs">
            <h2>Top up pixles</h2>
            {!pack ? (
              <p className="billing-status">
                Pixle packs are unavailable right now.
              </p>
            ) : (
              <>
                <p className="billing-plan-detail">
                  {pack.pixelsPerPack !== null
                    ? `${pack.pixelsPerPack.toLocaleString()} pixles per pack`
                    : 'Pixle pack'}{' '}
                  · {formatMoney(pack.pricePerPack, null)} per pack · ≈ 2–3 min
                  of video generation per pack*
                </p>
                {trialBlocked ? (
                  <>
                    <p className="billing-status">
                      Top-ups are blocked while your trial is active — subscribe
                      to Pro to continue.
                    </p>
                    <button
                      type="button"
                      className="billing-primary"
                      onClick={() => handleSubscribe(defaultPlanId)}
                      disabled={busyAction === `subscribe:${defaultPlanId}`}
                    >
                      {busyAction === `subscribe:${defaultPlanId}`
                        ? 'Redirecting…'
                        : 'Subscribe to Pro'}
                    </button>
                  </>
                ) : (
                  <div className="billing-pack-row">
                    <div className="billing-stepper" aria-label="Pack quantity">
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => clampQuantity(q - 1))}
                        disabled={clampQuantity(quantity) <= MIN_PACK_QUANTITY}
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={MIN_PACK_QUANTITY}
                        max={MAX_PACK_QUANTITY}
                        value={quantity}
                        onChange={(e) =>
                          setQuantity(clampQuantity(Number(e.target.value)))
                        }
                        aria-label="Pack quantity (1 to 25)"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => clampQuantity(q + 1))}
                        disabled={clampQuantity(quantity) >= MAX_PACK_QUANTITY}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="billing-primary"
                      onClick={handleBuyPacks}
                      disabled={busyAction === 'buy'}
                    >
                      {busyAction === 'buy'
                        ? 'Redirecting…'
                        : `Buy ${clampQuantity(quantity)} pack${clampQuantity(quantity) === 1 ? '' : 's'}${packTotal !== null ? ` · ${formatMoney(packTotal, null)}` : ''}`}
                    </button>
                  </div>
                )}
                <p className="billing-reassurance">
                  *Video length is approximate — model and resolution dependent.
                </p>
              </>
            )}
          </section>
          <footer className="billing-footer">
            Secure payments powered by Stripe · <Link to="/terms">Terms of Service</Link>{' '}
            · <Link to="/privacy">Privacy Policy</Link> 
          </footer>
        </>
      )}
    </div>
  );
};

export default BillingPage;
